import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../register/components/ProgressBar';
import BirthdaySelector from './components/BirthdaySelector';
import GenderSelector from './components/GenderSelector';
import authService from '../../../services/authService';

const BirthdayGenderSelection = () => {
    const [birthday, setBirthday] = useState(null);
    const [gender, setGender] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [registrationData, setRegistrationData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Get data from previous steps
        const storedData = localStorage.getItem('registrationData');
        if (!storedData) {
            // If no previous data, redirect back to country selection
            navigate('/country-language-selection');
            return;
        }
        setRegistrationData(JSON.parse(storedData));
    }, [navigate]);

    const collectCompleteRegistrationData = (birthdayValue = null, genderValue = 'PREFER_NOT_TO_SAY') => {
        if (!registrationData) {
            throw new Error('Registration data not found');
        }

        return {
            // Basic registration fields
            email: registrationData.email,
            password: registrationData.password,
            password2: registrationData.password, // API expects password confirmation
            first_name: registrationData.firstName,
            last_name: registrationData.lastName,

            // Country and language from first step
            country: registrationData.country?.code || registrationData.country,
            language_preference: registrationData.language?.code || null,

            // Terms acceptance (frontend sends both fields with same value)
            terms_accepted: true,
            privacy_policy_accepted: true,

            // Birthday and gender from current step
            birth_date: birthdayValue || null, // BirthdaySelector already returns YYYY-MM-DD format
            gender: genderValue
        };
    };

    const handleContinue = async () => {
        setIsLoading(true);
        setErrors({});

        try {
            const completeRegistrationData = collectCompleteRegistrationData(
                birthday,
                gender || 'PREFER_NOT_TO_SAY'
            );

            console.log('🚀 Creating user account with:', completeRegistrationData);

            const response = await authService.register(completeRegistrationData);

            // Clear registration data from localStorage
            localStorage.removeItem('registrationData');

            // Navigate to email verification page
            navigate('/auth/verify', {
                state: {
                    email: completeRegistrationData.email,
                    message: 'Registration successful! Please check your email to verify your account.'
                }
            });

        } catch (error) {
            console.error('Registration failed:', error);
            setErrors({ general: error.message || 'Registration failed. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSkip = async () => {
        setIsLoading(true);
        setErrors({});

        try {
            const completeRegistrationData = collectCompleteRegistrationData(null, 'PREFER_NOT_TO_SAY');

            console.log('🚀 Creating user account with skip option:', completeRegistrationData);

            const response = await authService.register(completeRegistrationData);

            // Clear registration data from localStorage
            localStorage.removeItem('registrationData');

            // Navigate to email verification page
            navigate('/auth/verify', {
                state: {
                    email: completeRegistrationData.email,
                    message: 'Registration successful! Please check your email to verify your account.'
                }
            });

        } catch (error) {
            console.error('Registration failed:', error);
            setErrors({ general: error.message || 'Registration failed. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    if (!registrationData) {
        return <div>Loading...</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <ProgressBar progress={75} />

                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-900">
                                As a valuable Giftsy member let us celebrate your birthday!
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                Help us personalize your gift experience (optional)
                            </p>
                        </div>

                        {errors.general && (
                            <div className="rounded-md bg-red-50 p-4">
                                <div className="text-sm text-red-700">
                                    {errors.general}
                                </div>
                            </div>
                        )}

                        <div className="space-y-6">
                            <BirthdaySelector
                                selectedBirthdate={birthday}
                                onBirthdateChange={setBirthday}
                            />

                            <GenderSelector
                                selectedGender={gender}
                                onGenderChange={setGender}
                            />
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={handleContinue}
                                disabled={isLoading}
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Creating Account...' : 'Continue'}
                            </button>

                            <div className="text-center">
                                <button
                                    onClick={handleSkip}
                                    disabled={isLoading}
                                    className="text-sm text-orange-600 hover:text-orange-500 disabled:opacity-50"
                                >
                                    Skip for now
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BirthdayGenderSelection; 